import type { ApiErrorInfo, ApiProviderMigrationInventoryItem } from "@/lib/api";
import { InfoBanner } from "@/components/ui/banner";
import { ErrorState } from "@/components/ui/system-state";
import { SupportDetails } from "@/components/ui/copyable-reference";
import type { ProviderMigrationInventory } from "@/hooks/use-provider-migration";

export function ReadOnlyBanner() {
  return (
    <InfoBanner variant="info" title="Read-only, no funds moved">
      This journey only reads a provider inventory snapshot. Nothing on these pages can move funds, change the
      provider workspace, or start a migration. Provider data shown here is testnet/sandbox evidence unless the
      environment label says otherwise.
    </InfoBanner>
  );
}

export function UnavailableWithRetry({
  title,
  error,
  onRetry,
}: {
  title: string;
  error: ApiErrorInfo;
  onRetry: () => void;
}) {
  // A failed live call is an error with a retry, not "nothing has been wired here" (ALL-5739).
  // The reason code is never the message; the server support reference is shown beside it.
  return (
    <div className="flex flex-col items-center gap-2">
      <ErrorState
        title={title}
        description="We couldn't read the provider inventory just now. Try again, or contact support with the reference below."
        onRetry={onRetry}
      />
      <SupportDetails reference={error.supportReference} capability={error.capability} className="justify-center" />
    </div>
  );
}

/** Freshness is derived from the snapshot's own freshUntil, never assumed. */
export function snapshotFreshness(freshUntil: string | null, now: number = Date.now()): "fresh" | "stale" | "unknown" {
  if (!freshUntil) return "unknown";
  const t = Date.parse(freshUntil);
  if (Number.isNaN(t)) return "unknown";
  return t >= now ? "fresh" : "stale";
}

export function countByResourceType(inventory: ProviderMigrationInventory | null): Record<string, number> {
  const counts: Record<string, number> = {};
  for (const item of inventory?.items ?? []) counts[item.resourceType] = (counts[item.resourceType] ?? 0) + 1;
  return counts;
}

/**
 * One provider attribute as display text, exactly as the gateway sent it. A missing, empty or
 * non-string value is null so the page renders "not available" instead of inventing one. Balances
 * stay strings: parsing to a float would round 18-decimal amounts.
 */
export function inventoryAttribute(item: ApiProviderMigrationInventoryItem, key: string): string | null {
  const value = item.attributes?.[key];
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  return trimmed ? trimmed.slice(0, 256) : null;
}

export const CATEGORY_LABEL = {
  VAULT_ACCOUNTS: "Vault accounts",
  ASSET_WALLETS: "Asset wallets",
  VAULT_ADDRESSES: "Deposit addresses",
} as const;
